import { useState } from "react";
import { motion } from "framer-motion";
import {
  Phone,
  EnvelopeSimple,
  MapPin,
  Globe,
  Clock,
  CheckCircle,
  Warning,
  SpinnerGap,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const contactInfo = [
  {
    icon: Phone,
    title: "Phone",
    content: "+256 782 565 502\n+256 761 834 803",
    href: "tel:+256782565502",
  },
  {
    icon: EnvelopeSimple,
    title: "Email",
    content: "info@pedafrica.org",
    href: "mailto:info@pedafrica.org",
  },
  {
    icon: MapPin,
    title: "Address",
    content: "Bombo, Luweero District\nUganda",
    href: "https://maps.google.com/?q=Bombo+Luweero+District+Uganda",
  },
  {
    icon: Globe,
    title: "Website",
    content: "www.pedafrica.org",
    href: null,
  },
];

const officeHours = [
  { day: "Monday - Friday", hours: "8:00 AM - 5:00 PM" },
  { day: "Saturday", hours: "9:00 AM - 1:00 PM" },
  { day: "Sunday", hours: "Closed" },
];

export default function ContactPage() {
  const [formData, setFormData] = useState({
    full_name: "",
    organization: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ type: "", message: "" });
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.full_name.trim() || formData.full_name.length < 2) {
      newErrors.full_name = "Full name is required (minimum 2 characters)";
    }
    
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    
    if (!formData.subject.trim() || formData.subject.length < 5) {
      newErrors.subject = "Subject is required (minimum 5 characters)";
    }
    
    if (!formData.message.trim() || formData.message.length < 10) {
      newErrors.message = "Message is required (minimum 10 characters)";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    setStatus({ type: "", message: "" });
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setStatus({ type: "success", message: data.message });
        setFormData({
          full_name: "",
          organization: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });
      } else {
        setStatus({ type: "error", message: data.detail || "Failed to submit. Please try again." });
      }
    } catch (err) {
      setStatus({ type: "error", message: "Network error. Please check your connection and try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-green-950" data-testid="contact-hero">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 pattern-dots" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400 mb-4 block">
              Contact Us
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-6">
              Get In Touch
            </h1>
            <p className="text-lg text-green-100/80 leading-relaxed">
              We'd love to hear from you. Whether you have a question about our programs, partnership opportunities, or anything else, our team is ready to help.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-white border-b border-slate-100" data-testid="contact-info-cards">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {contactInfo.map((info, index) => {
              const Tag = info.href ? motion.a : motion.div;
              const linkProps = info.href ? {
                href: info.href,
                target: info.title === "Address" ? "_blank" : undefined,
                rel: info.title === "Address" ? "noopener noreferrer" : undefined,
              } : {};
              return (
                <Tag
                  key={info.title}
                  {...linkProps}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-slate-50 rounded-2xl p-6 hover:bg-green-50 transition-colors group"
                  data-testid={`contact-info-${info.title.toLowerCase()}`}
                >
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-green-700 transition-colors">
                    <info.icon className="h-6 w-6 text-green-700 group-hover:text-white transition-colors" weight="duotone" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">{info.title}</h3>
                  <p className="text-sm text-slate-600 whitespace-pre-line">{info.content}</p>
                </Tag>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form & Details */}
      <section className="py-20 md:py-28" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1fr_1.2fr] gap-12">
            {/* Left Column - Info */}
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">
                Reach Out
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-6">
                Let's Work Together for Sustainable Development
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Connect with PEDA to explore partnerships, enterprise support, research collaboration, leadership development, or community-based innovation programs.
              </p>

              {/* Office Hours */}
              <div className="bg-slate-50 rounded-2xl p-6 mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="h-6 w-6 text-green-700" weight="duotone" />
                  <h3 className="font-semibold text-slate-900">Office Hours</h3>
                </div>
                <div className="space-y-2">
                  {officeHours.map((item) => (
                    <div key={item.day} className="flex justify-between text-sm">
                      <span className="text-slate-600">{item.day}</span>
                      <span className="font-medium text-slate-900">{item.hours}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Map Placeholder */}
              <div className="bg-slate-100 rounded-2xl h-64 flex items-center justify-center">
                <div className="text-center">
                  <MapPin className="h-12 w-12 text-slate-400 mx-auto mb-2" weight="duotone" />
                  <p className="text-slate-500">Plot 62 Bombo Road, Kampala-Uganda</p>
                  <a 
                    href="https://maps.google.com/?q=Bombo+Road+Kampala+Uganda" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-green-700 hover:underline text-sm mt-2 inline-block"
                  >
                    Open in Google Maps
                  </a>
                </div>
              </div>
            </div>

            {/* Right Column - Form */}
            <div className="bg-white rounded-3xl border border-slate-200 p-8 shadow-lg" data-testid="contact-form-wrapper">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Send a Message</h3>
              
              {/* Status Messages */}
              {status.message && (
                <div 
                  className={`mb-6 p-4 rounded-xl flex items-start gap-3 ${
                    status.type === "success" 
                      ? "bg-green-50 text-green-800 border border-green-200" 
                      : "bg-red-50 text-red-800 border border-red-200"
                  }`}
                  data-testid="form-status-message"
                >
                  {status.type === "success" ? (
                    <CheckCircle className="h-5 w-5 shrink-0 mt-0.5" weight="fill" />
                  ) : (
                    <Warning className="h-5 w-5 shrink-0 mt-0.5" weight="fill" />
                  )}
                  <p>{status.message}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="full_name" className="text-slate-700 mb-2 block">
                      Full Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="full_name"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      className={`h-12 rounded-xl ${errors.full_name ? "border-red-500" : ""}`}
                      data-testid="contact-full-name-input"
                    />
                    {errors.full_name && (
                      <p className="text-red-500 text-sm mt-1">{errors.full_name}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="organization" className="text-slate-700 mb-2 block">
                      Organization
                    </Label>
                    <Input
                      id="organization"
                      name="organization"
                      value={formData.organization}
                      onChange={handleChange}
                      placeholder="Company / Organization"
                      className="h-12 rounded-xl"
                      data-testid="contact-organization-input"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email" className="text-slate-700 mb-2 block">
                      Email Address <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      className={`h-12 rounded-xl ${errors.email ? "border-red-500" : ""}`}
                      data-testid="contact-email-input"
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-slate-700 mb-2 block">
                      Phone Number
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+256 XXX XXX XXX"
                      className="h-12 rounded-xl"
                      data-testid="contact-phone-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="subject" className="text-slate-700 mb-2 block">
                    Subject <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="What is this about?"
                    className={`h-12 rounded-xl ${errors.subject ? "border-red-500" : ""}`}
                    data-testid="contact-subject-input"
                  />
                  {errors.subject && (
                    <p className="text-red-500 text-sm mt-1">{errors.subject}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="message" className="text-slate-700 mb-2 block">
                    Message <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your project, inquiry, or how we can help..."
                    className={`min-h-[150px] rounded-xl ${errors.message ? "border-red-500" : ""}`}
                    data-testid="contact-message-input"
                  />
                  {errors.message && (
                    <p className="text-red-500 text-sm mt-1">{errors.message}</p>
                  )}
                </div>

                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full bg-green-700 hover:bg-green-800 rounded-xl h-14"
                  disabled={loading}
                  data-testid="contact-submit-button"
                >
                  {loading ? (
                    <>
                      <SpinnerGap className="h-5 w-5 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    "Submit Inquiry"
                  )}
                </Button>

                <p className="text-sm text-slate-500 text-center">
                  We typically respond within 24-48 hours.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Preview */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="contact-faq">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">
              FAQs
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-4">
            {[
              {
                q: "What does PEDA AFRICA do?",
                a: "PEDA AFRICA is a research-driven organization focused on sustainable socio-economic development in Africa through entrepreneurship, innovation, capacity building, and community-led enterprises.",
              },
              {
                q: "Who does PEDA AFRICA work with?",
                a: "PEDA AFRICA works closely with communities, local entrepreneurs, enterprises, development partners, institutions, and other stakeholders to create practical, lasting solutions tailored to local needs.",
              },
              {
                q: "What makes PEDA AFRICA different?",
                a: "PEDA AFRICA combines empirical research, targeted training, leadership development, and technology-focused support to help communities turn innovative ideas into impactful projects and enterprises.",
              },
              {
                q: "What is the long-term goal?",
                a: "PEDA AFRICA aims to help create a sustainable future generation free from poverty by building resilient communities, promoting job creation, and strengthening inclusive economic growth across Africa.",
              },
            ].map((faq, index) => (
              <motion.div
                key={faq.q}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm"
              >
                <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
